/* eslint-disable indent */
import React, { useContext } from 'react';

import { Map } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl, { setWorkerUrl } from 'maplibre-gl';

// Fix for CRA production builds: webpack breaks maplibre's inline blob worker.
// Use the pre-built CSP worker served as a static file instead.
setWorkerUrl(`${process.env.PUBLIC_URL}/maplibre-gl-csp-worker.js`);

import { bbox, center } from '@turf/turf';

// The default maplibre-gl bundle embeds its worker code as a string that gets
// eval'd via a Blob URL. CRA's production minifier mangles shared closure
// variable names between the main bundle and that string, causing
// `ReferenceError: <mangled name> is not defined` inside the worker in prod
// builds only. The CSP build loads the worker from a real, unminified static
// file instead, which avoids the shared-scope mangling entirely.
maplibregl.setWorkerUrl(
  `${process.env.PUBLIC_URL || ''}/maplibre-gl-csp-worker.js`,
);

import { MapLayers } from './MapLayers';
import { useResponsive } from '../hooks/useResponsive';
import { PopupLayer } from './PopupLayer';
import { FeatureSelectedHighlight } from './FeatureSelectedHighlight';
import { ExploreContext } from '../context/ExploreContext';
import { baseMaps } from '../enum/baseMaps';

export const MapApp = () => {
  const { isDesktop } = useResponsive();

  const {
    base,
    layerConfig,
    offsetMap,
    drawnGeo,
    describedGeo,
    theoreticalGeo,
    noSideWalkGeo,
    pathWalkGeo,
    pathWalkCycleGeo,
    crossingGeo,
    crossingWayGeo,
    handleSetCalc,
    content,
  } = useContext(ExploreContext);

  const mapRef = React.useRef();
  const geolocateControlRef = React.useRef();
  const navigationControlRef = React.useRef();

  const [mapIsLoaded, setMapIsLoaded] = React.useState(false);
  const [listVisible, setListVisible] = React.useState([]);
  const [cursor, setCursor] = React.useState('auto');
  const [selectedFeature, setSelectedFeature] = React.useState(undefined);
  const [showPopup, setShowPopup] = React.useState(false);
  const [lngPopup, setLngPopup] = React.useState(null);
  const [latPopup, setLatPopup] = React.useState(null);

  const baseLayers = baseMaps.layers.map((element) => {
    return element.id;
  });

  const handleSelectedFeature = (feature) => {
    setSelectedFeature(feature);
  };

  const handleMouseUp = (e) => {
    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }
    const existingVisibleLayers = listVisible.filter(
      (layerId) => map.getLayer(layerId) !== undefined,
    );
    if (existingVisibleLayers.length === 0) {
      return;
    }
    const features = map.queryRenderedFeatures(e.point, {
      layers: existingVisibleLayers,
    });

    if (features && features.length > 0) {
      if (showPopup) {
        setShowPopup(false);
      }
      setLngPopup(e.lngLat.lng);
      setLatPopup(e.lngLat.lat);
      const featureToAdd = {
        type: 'Feature',
        layer: features[0].layer,
        source: features[0].source,
        geometry: features[0].geometry,
        properties: features[0].properties,
      };
      setSelectedFeature(featureToAdd);
      setShowPopup(true);
      const centerPoint = center(features[0]);
      if (centerPoint) {
        const coord = centerPoint.geometry.coordinates;
        mapRef.current?.flyTo({ center: coord });
      } else {
        setSelectedFeature(undefined);
      }
    }
  };

  const onMouseEnter = React.useCallback(() => setCursor('pointer'), []);
  const onMouseLeave = React.useCallback(() => setCursor('auto'), []);

  const handleVisibilityLayer = () => {
    let map = mapRef.current?.getMap();
    for (const key in layerConfig) {
      if (
        map.getLayer(key) !== undefined &&
        mapRef.current?.getLayer !== null
      ) {
        map.setLayoutProperty(
          key,
          'visibility',
          layerConfig[key].visibility === 'visible' ? 'visible' : 'none',
        );
      }
    }
  };

  const handleOnZoomSetCalc = () => {
    if (mapRef.current && mapRef.current.getZoom() > 13) {
      handleSetCalc(true);
    } else {
      handleSetCalc(false);
    }
  };

  /**
   * load base visibility
   */
  const loadBase = () => {
    for (let element of baseLayers) {
      if (mapRef.current) {
        if (element !== base) {
          mapRef.current
            .getMap()
            .setLayoutProperty(element, 'visibility', 'none');
        } else {
          mapRef.current
            .getMap()
            .setLayoutProperty(element, 'visibility', 'visible');
        }
      }
    }
  };

  React.useEffect(() => {
    loadBase();
  }, [base]);

  React.useEffect(() => {
    if (theoreticalGeo && theoreticalGeo.features.length > 0) {
      const bounds = bbox(theoreticalGeo);

      if (bounds && mapRef.current) {
        mapRef.current.fitBounds(bounds, {
          maxZoom: 18,
          padding: { bottom: isDesktop ? null : offsetMap + 50 },
        });
      }
    } else {
      return;
    }
  }, [theoreticalGeo, isDesktop, offsetMap]);

  React.useEffect(() => {
    return () => {
      if (showPopup) {
        setShowPopup(false);
      }
      setSelectedFeature(undefined);
    };
  }, [theoreticalGeo]);

  React.useEffect(() => {
    if (mapRef.current !== undefined && mapRef.current !== null) {
      mapRef.current.once('load', () => setMapIsLoaded(true));
      // Disable rotation on phone
      mapRef.current.getMap().touchZoomRotate.disableRotation();
    }
  }, [mapRef.current]);

  React.useEffect(() => {
    if (mapIsLoaded) {
      if (mapRef.current !== undefined && mapRef.current !== null) {
        // Disable rotation on phone
        mapRef.current?.getMap().touchZoomRotate.disableRotation();
      }
    }
  }, [mapIsLoaded]);

  React.useEffect(() => {
    let layerInteractiveList = Object.keys(layerConfig).filter(
      (elem) => layerConfig[elem].visibility === 'visible',
    );
    setListVisible([...layerInteractiveList]);
  }, [layerConfig]);

  React.useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    const mapInstance = mapRef.current.getMap();
    if (!mapInstance) {
      return;
    }

    const geolocateControl = new maplibregl.GeolocateControl({
      trackUserLocation: true,
      showUserHeading: true,
      positionOptions: { enableHighAccuracy: true },
    });
    geolocateControlRef.current = geolocateControl;

    const navigationControl = new maplibregl.NavigationControl({
      showCompass: true,
    });
    navigationControlRef.current = navigationControl;

    // Add control with position based on isMobile
    mapInstance.addControl(
      geolocateControl,
      isDesktop ? 'bottom-left' : 'top-left',
    );
    mapInstance.addControl(
      navigationControl,
      isDesktop ? 'bottom-left' : 'top-left',
    );
    return () => {
      if (geolocateControlRef.current) {
        mapInstance.removeControl(geolocateControlRef.current);
      }
      if (navigationControlRef.current) {
        mapInstance.removeControl(navigationControlRef.current);
      }
    };
  }, [isDesktop, mapRef.current]);

  React.useEffect(() => {
    if (showPopup) {
      setShowPopup(false);
      setLatPopup(null);
      setLngPopup(null);
      setSelectedFeature(undefined);
    }
  }, [content]);

  return (
    <Map
      id='mymap'
      ref={mapRef}
      mapLib={maplibregl}
      mapStyle={baseMaps}
      cursor={cursor}
      interactiveLayerIds={listVisible}
      onLoad={handleVisibilityLayer}
      onMouseUp={handleMouseUp}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onZoom={handleOnZoomSetCalc}
      initialViewState={{
        latitude: 46.702202,
        longitude: 2.318115,
        zoom: 6,
      }}
      styleDiffing
    >
      {drawnGeo &&
        describedGeo &&
        theoreticalGeo &&
        pathWalkGeo &&
        pathWalkCycleGeo &&
        crossingGeo &&
        crossingWayGeo &&
        noSideWalkGeo && (
          <MapLayers
            drawnGeo={drawnGeo}
            describedGeo={describedGeo}
            theoreticalGeo={theoreticalGeo}
            noSidewalkGeo={noSideWalkGeo}
            pathWalkGeo={pathWalkGeo}
            pathCycleWalkGeo={pathWalkCycleGeo}
            crossGeo={crossingGeo}
            crossWayGeo={crossingWayGeo}
            layerConf={layerConfig}
          />
        )}
      {showPopup && (
        <PopupLayer
          lngPopup={lngPopup}
          latPopup={latPopup}
          feature={selectedFeature}
          closePopup={() => setShowPopup(false)}
          handleFeature={handleSelectedFeature}
        />
      )}
      {selectedFeature && (
        <FeatureSelectedHighlight feature={selectedFeature} />
      )}
    </Map>
  );
};
